import React, { useState, useRef, useEffect } from 'react';

export function ChipSelect({ options, selected, onChange, placeholder = 'Type to search...', emptyMessage }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const blurTimeout = useRef(null);

    const unselected = options.filter(o => !selected.includes(o.value));
    const filtered = unselected.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase())
    );

    useEffect(() => {
        setHighlightIndex(0);
    }, [query]);

    const addItem = (value) => {
        onChange([...selected, value]);
        setQuery('');
        inputRef.current?.focus();
    };

    const removeItem = (value) => {
        onChange(selected.filter(v => v !== value));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (open && filtered.length > 0) {
                addItem(filtered[highlightIndex]?.value ?? filtered[0].value);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (open && filtered.length > 0) {
                setHighlightIndex(i => (i + 1) % filtered.length);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (open && filtered.length > 0) {
                setHighlightIndex(i => (i - 1 + filtered.length) % filtered.length);
            }
        } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
            removeItem(selected[selected.length - 1]);
        } else if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
        }
    };

    const handleFocus = () => {
        clearTimeout(blurTimeout.current);
        setOpen(true);
    };

    const handleBlur = () => {
        blurTimeout.current = setTimeout(() => {
            setOpen(false);
            setQuery('');
        }, 150);
    };

    const getLabel = (value) => {
        const opt = options.find(o => o.value === value);
        return opt ? opt.label : value;
    };

    if (emptyMessage && options.length === 0) {
        return (
            <div className="relative">
                <small className="text-slate-400 dark:text-slate-500">{emptyMessage}</small>
            </div>
        );
    }

    return (
        <div className="relative" ref={containerRef}>
            <div
                className="flex flex-wrap gap-1.5 items-center min-h-[36px] sm:min-h-[44px] px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-text"
                onClick={() => inputRef.current?.focus()}
            >
                {selected.map(val => (
                    <span
                        key={val}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 text-xs font-medium"
                    >
                        {getLabel(val)}
                        <button
                            type="button"
                            className="text-accent-500 hover:text-accent-700 dark:hover:text-accent-200 ml-0.5"
                            onClick={(e) => { e.stopPropagation(); removeItem(val); }}
                            aria-label={`Remove ${getLabel(val)}`}
                        >
                            &times;
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    className="flex-1 min-w-[80px] outline-none bg-transparent text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={selected.length === 0 ? placeholder : ''}
                />
            </div>
            {open && (
                <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                    {filtered.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-slate-400">
                            {query ? 'No matches' : 'All selected'}
                        </li>
                    ) : (
                        filtered.map((opt, i) => (
                            <li
                                key={opt.value}
                                className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                                    i === highlightIndex
                                        ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    addItem(opt.value);
                                }}
                                onMouseEnter={() => setHighlightIndex(i)}
                            >
                                {opt.label}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}
